import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { DEFAULT_DCUI_CONTENT, DEFAULT_DCUI_THEME } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import {
    DCUI_CONTENT_PROPERTY_TYPE_ID,
    DCUI_THEME_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { DCUIThemingValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { DCUIContent, DCUITheme, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { FormattedIdwProfile } from "@idw-types/data/idw-profile";
import { FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { setValueAndAddedToProfileForProfileProperty } from "@utils/login-hint/login-hint-utils";
import { generateContentProfileProperty } from "@utils/content-util";
import { ThemingSubTabContainer } from "../../../common/theming-sub-tab-container/theming-sub-tab-container";
import { DCUIThemingThemeTabContainer } from "../../../verification-service-details/verification-service-details-tab-container/verification-service-details-dcui-theming-tab/dcui-theming-theme-tab-container/dcui-theming-theme-tab-container";
import { DCUIThemingStartPageTabContainer } from "../../../verification-service-details/verification-service-details-tab-container/verification-service-details-dcui-theming-tab/dcui-theming-start-page-tab-container/dcui-theming-start-page-tab-container";
import { DCUIThemingCompleteTabContainer } from "../../../verification-service-details/verification-service-details-tab-container/verification-service-details-dcui-theming-tab/dcui-theming-complete-container/dcui-theming-complete-container";
import { DCUIThemingErrorTabContainer } from "../../../verification-service-details/verification-service-details-tab-container/verification-service-details-dcui-theming-tab/dcui-theming-error-tab-container/dcui-theming-error-tab-container";
import { DCUIThemingTextContentTabContainer } from "../../../verification-service-details/verification-service-details-tab-container/verification-service-details-dcui-theming-tab/dcui-theming-text-content-tab-container/dcui-theming-text-content-tab-container";
import { DCUIThemingStepTabContainer } from "../../../verification-service-details/verification-service-details-tab-container/verification-service-details-dcui-theming-tab/dcui-theming-step-tab-container/dcui-theming-step-tab-container";
import { useMemo } from "react";

type WorkflowDetailsDCUIThemingTabProps = {
    isDisabled: boolean;
    selectedLanguage: LocaleTypes;
    availableLanguages: LocaleTypes[];
    isMultiLanguageEnabled: boolean;
    documentCaptureSteps: Array<DCUIThemingStep>;
    multiLanguageDCUIContent: MultiLanguageWrapper<DCUIContent>;
    validationErrors: DCUIThemingValidationErrors;
    inheritedDCUIContent: DCUIContent;
    dcuiContent: DCUIContent;
    dcuiTheme: DCUITheme;
    workflowProfile: FormattedIdwProfile;
    workflowDetailsConfigurationState: FormattedWorkflowDetailsConfiguration;
    setWorkflowProfile: (workflowProfile: FormattedIdwProfile) => void;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
};

export const WorkflowDetailsDCUIThemingTab = (props: WorkflowDetailsDCUIThemingTabProps) => {
    const {
        isDisabled,
        selectedLanguage,
        availableLanguages,
        isMultiLanguageEnabled,
        documentCaptureSteps,
        multiLanguageDCUIContent,
        validationErrors,
        inheritedDCUIContent,
        dcuiContent,
        dcuiTheme,
        workflowProfile,
        workflowDetailsConfigurationState,
        setWorkflowProfile,
        uploadFileToS3
    } = props;

    const permissionBasedIsDisabled = useMemo(() => {
        return (
            isDisabled ||
            !workflowDetailsConfigurationState.permissions.canModifyDCUICustomization ||
            !workflowDetailsConfigurationState.permissions.canModifyBranding
        );
    }, [
        isDisabled,
        workflowDetailsConfigurationState.permissions.canModifyDCUICustomization,
        workflowDetailsConfigurationState.permissions.canModifyBranding
    ]);

    const updateDCUIContentPropertyValue = (newDCUIContent: DCUIContent) => {
        const newProperties = workflowProfile.profileProperties.map(property => {
            if (property.availableProperty?.id === DCUI_CONTENT_PROPERTY_TYPE_ID) {
                return generateContentProfileProperty(property, isMultiLanguageEnabled, multiLanguageDCUIContent, availableLanguages, selectedLanguage, newDCUIContent, DEFAULT_DCUI_CONTENT);
            }

            return property;
        });

        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties
        });
    };

    const updateDCUIThemePropertyValue = (dcuiTheme: DCUITheme) => {
        const newValue = JSON.stringify(dcuiTheme);
        const newProperties = workflowProfile.profileProperties.map(property => {
            if (property.availableProperty?.id === DCUI_THEME_PROPERTY_TYPE_ID) {
                return setValueAndAddedToProfileForProfileProperty(property, newValue, true);
            }
            return property;
        });
        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties
        });
    };

    return (
        <ThemingSubTabContainer>
            <Tabs id="document-capture" unmountOnExit={true}>
                <Tab eventKey={DCUIThemingTabTypes.THEME} title={validationErrors.theme.tabTitle}>
                    <DCUIThemingThemeTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.theme}
                        dcuiContent={dcuiContent}
                        inheritedDCUIContent={inheritedDCUIContent}
                        inheritedDCUITheme={DEFAULT_DCUI_THEME}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        dcuiTheme={dcuiTheme}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={PreviewEntityTypes.WORKFLOW}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                        updateDCUIThemePropertyValue={updateDCUIThemePropertyValue}
                        uploadFileToS3={uploadFileToS3}
                    />
                </Tab>
                <Tab eventKey={DCUIThemingTabTypes.START_PAGE} title={validationErrors.startPage.tabTitle}>
                    <DCUIThemingStartPageTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.startPage}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        inheritedDCUIContent={inheritedDCUIContent}
                        uploadFileToS3={uploadFileToS3}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={PreviewEntityTypes.WORKFLOW}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                    />
                </Tab>
                {documentCaptureSteps.map(step => (
                    <Tab eventKey={step.tabType} title={validationErrors[step.stepType].tabTitle} key={step.tabType}>
                        <DCUIThemingStepTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors[step.stepType]}
                            dcuiContent={dcuiContent}
                            inheritedDCUIContent={inheritedDCUIContent}
                            pageLevel={PageLevelTypes.WORKFLOW}
                            dcuiTheme={dcuiTheme}
                            tabType={step.tabType}
                            stepType={step.stepType}
                            stepResources={step.stepResources}
                            documentCaptureSteps={documentCaptureSteps}
                            previewEntityType={PreviewEntityTypes.WORKFLOW}
                            updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                            uploadFileToS3={uploadFileToS3}
                        />
                    </Tab>
                ))}
                <Tab eventKey={DCUIThemingTabTypes.CAPTURE_COMPLETE} title={validationErrors.captureComplete.tabTitle}>
                    <DCUIThemingCompleteTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.captureComplete}
                        dcuiContent={dcuiContent}
                        inheritedDCUIContent={inheritedDCUIContent}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        dcuiTheme={dcuiTheme}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={PreviewEntityTypes.WORKFLOW}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                    />
                </Tab>
                <Tab eventKey={DCUIThemingTabTypes.ERROR} title={validationErrors.errorPage.tabTitle}>
                    <DCUIThemingErrorTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.errorPage}
                        dcuiContent={dcuiContent}
                        inheritedDCUIContent={inheritedDCUIContent}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        dcuiTheme={dcuiTheme}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={PreviewEntityTypes.WORKFLOW}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                    />
                </Tab>
                <Tab eventKey={DCUIThemingTabTypes.TEXT_CONTENT} title={validationErrors.textContent.tabTitle}>
                    <DCUIThemingTextContentTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.textContent}
                        dcuiContent={dcuiContent}
                        inheritedDCUIContent={inheritedDCUIContent}
                        pageLevel={PageLevelTypes.WORKFLOW}
                        activeAcuantProperties={{
                            hasValidAcuantDPIValidationProperty: true,
                            hasValidAcuantGlareValidationProperty: true,
                            hasValidAcuantSDKCaptureProperty: true,
                            hasValidAcuantSharpnessValidationProperty: true,
                            hasValidLivenessApikey: true
                        }}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                    />
                </Tab>
            </Tabs>
        </ThemingSubTabContainer>
    );
};